import { useEffect, useState, useCallback } from "react";
import { CircleMarker, Popup } from "react-leaflet";
import LeafletMap, { FitBounds } from "@/components/map/LeafletMap";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Search, Map as MapIcon, List, MapPin, MapPinned, Save, Wrench, Route, Download } from "lucide-react";
import { toast } from "sonner";
import PhotoUpload from "@/components/PhotoUpload";
import { Checkbox } from "@/components/ui/checkbox";
import { MailingLabelsDialog } from "@/components/labels/MailingLabelsDialog";
import { FillZipsDialog } from "@/components/labels/FillZipsDialog";
import { Tags, History } from "lucide-react";
import { ContactHistoryDialog } from "@/components/admin/ContactHistoryDialog";
import { ImportProspectsDialog } from "@/components/admin/ImportProspectsDialog";
import { BulkEmailDialog } from "@/components/marketing/BulkEmailDialog";
import { Upload, Mails } from "lucide-react";

const ALL = "__all__";
const CLOSED = new Set(["Interested", "Signed Up", "Not Interested"]);
const TODAY = new Date().toLocaleDateString("en-CA");

const TYPE_COLORS = { "Bike Shop": "#22c55e", Restaurant: "#f97316", Coffee: "#d97706", Brewery: "#eab308", Hotel: "#3b82f6", Attraction: "#a855f7", Other: "#94a3b8" };
const EMPTY = { name: "", address: "", phone: "", email: "", contact_person: "", type: "Other", bike_path_id: "", zone_id: "", status: "New", notes: "", last_contacted: "", latitude: "", longitude: "" };
const OUTREACH_KEYS = ["email", "contact_person", "notes", "last_contacted"];

export default function ProspectsPanel({ followUpDue = false }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [filters, setFilters] = useState({ states: [], bikePaths: [], zones: [], types: [], statuses: [] });
  const [f, setF] = useState({ state: ALL, bike_path_id: ALL, zone_number: ALL, type: "Restaurant", status: ALL, quality: ALL, filled: ALL, follow_up: followUpDue ? "due" : ALL });
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {mode:'add'|'edit', form}
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("list");
  const [geocoding, setGeocoding] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const [edits, setEdits] = useState({}); // {id: {email, contact_person, notes, last_contacted}} — local until Save Changes
  const [savingBatch, setSavingBatch] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [corridorOpen, setCorridorOpen] = useState(false);
  const [corridorBp, setCorridorBp] = useState("");
  const [corridorData, setCorridorData] = useState(null);
  const [corridorLoading, setCorridorLoading] = useState(false);
  const [harvesting, setHarvesting] = useState(false);
  const [report, setReport] = useState(null);
  const [riderBusy, setRiderBusy] = useState(null);
  const [riderPhotos, setRiderPhotos] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);

  const [zipsOpen, setZipsOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkEmail, setBulkEmail] = useState(null);
  useEffect(() => { setSelected(new Set()); }, [rows]);
  const toggleRow = (id, on) => setSelected((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const markChecked = async (r) => {
    try {
      await api.post(`/prospects/${r.id}/address-checked`);
      setRows((rs) => (f.filled !== ALL && f.filled !== "checked" ? rs.filter((x) => x.id !== r.id) : rs.map((x) => (x.id === r.id ? { ...x, addressChecked: true } : x))));
      toast.success("Address marked as checked");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  useEffect(() => { api.get("/prospects/filters").then(({ data }) => setFilters(data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (f.state !== ALL) params.state = f.state;
      if (f.bike_path_id !== ALL) params.bike_path_id = f.bike_path_id;
      if (f.zone_number !== ALL) params.zone_number = f.zone_number;
      if (f.type !== ALL) params.type = f.type;
      if (f.status !== ALL) params.status = f.status;
      if (f.quality !== ALL) params.quality = f.quality;
      if (f.filled !== ALL) params.filled = f.filled;
      if (f.follow_up !== ALL) params.follow_up = f.follow_up;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/prospects", { params });
      setRows(data.items || []);
      setEdits({}); // discard unsaved inline edits when the result set changes
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [f, q]);

  // Admin starts with a BLANK list to avoid loading tens of thousands of prospects
  // at once; it loads only once a filter/search is chosen. Partners auto-load their
  // (small, zone-scoped) list as before.
  const hasActiveFilter = f.state !== ALL || f.bike_path_id !== ALL || f.zone_number !== ALL || f.status !== ALL || f.quality !== ALL || f.filled !== ALL || f.follow_up !== ALL || q.trim() !== "";
  useEffect(() => {
    if (isAdmin && !hasActiveFilter) { setRows([]); setEdits({}); setLoading(false); return; }
    load();
  }, [load, isAdmin, hasActiveFilter]);

  const openAdd = () => setDialog({ mode: "add", form: { ...EMPTY } });
  const openEdit = (r) => setDialog({ mode: "edit", id: r.id, form: {
    name: r.name || "", address: r.address || "", phone: r.phone || "", email: r.email || "",
    contact_person: r.contactPerson || "", type: r.type || "Other", bike_path_id: r.bikePathId || "",
    zone_id: r.zoneId || "", status: r.status || "New", notes: r.notes || "",
    last_contacted: r.lastContacted ? String(r.lastContacted).slice(0, 10) : "",
    latitude: r.lat ?? "", longitude: r.lng ?? "",
  } });

  const upd = (k, v) => setDialog((d) => ({ ...d, form: { ...d.form, [k]: v } }));
  const setCoords = (lat, lng) => setDialog((d) => ({ ...d, form: { ...d.form, latitude: lat, longitude: lng } }));
  const geocode = async () => {
    const addr = (dialog?.form.address || "").trim();
    if (!addr) { toast.error("Enter an address first"); return; }
    setGeocoding(true);
    try {
      const { data } = await api.post("/prospects/geocode", { address: addr });
      if (data.found) { setCoords(data.lat, data.lng); setMapKey((k) => k + 1); toast.success("Location found from address"); }
      else toast.error("Address not found — click the map to drop a pin");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setGeocoding(false); }
  };

  const save = async () => {
    const form = dialog.form;
    if (!form.name.trim()) { toast.error("Business name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, zone_id: form.zone_id || null, bike_path_id: form.bike_path_id || null, last_contacted: form.last_contacted || null,
        latitude: form.latitude === "" || form.latitude == null ? null : Number(form.latitude),
        longitude: form.longitude === "" || form.longitude == null ? null : Number(form.longitude) };
      if (dialog.mode === "add") await api.post("/prospects", payload);
      else await api.put(`/prospects/${dialog.id}`, payload);
      toast.success(dialog.mode === "add" ? "Prospect added" : "Prospect updated");
      setDialog(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete prospect "${r.name}"?`)) return;
    try { await api.delete(`/prospects/${r.id}`); toast.success("Prospect deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // Turn an attraction prospect into a Content → Attraction (rider-map pin + banner).
  // Reuses the existing role-aware endpoints; shop-added items land pending admin approval.
  const addToRider = async (r) => {
    setRiderBusy(r.id);
    try {
      await api.post(isAdmin ? `/admin/prospects/${r.id}/save-to-rider` : `/my/prospects/${r.id}/save-to-rider`, { photos: riderPhotos });
      toast.success(riderPhotos.length ? "Added to the Rider map with your photo — pending admin approval." : "Added to the Rider map — pending admin approval. Add photos & details in Content → Attractions.");
      setRiderPhotos([]);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRiderBusy(null); }
  };
  const removeFromRider = async (r) => {
    setRiderBusy(r.id);
    try {
      await api.delete(isAdmin ? `/admin/prospects/${r.id}/rider` : `/my/prospects/${r.id}/rider`);
      toast.success("Removed from the Rider map");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRiderBusy(null); }
  };

  // --- Inline outreach editing (local until Save Changes) ---
  const orig = (r) => ({ email: r.email || "", contact_person: r.contactPerson || "", notes: r.notes || "", last_contacted: r.lastContacted ? String(r.lastContacted).slice(0, 10) : "" });
  const cell = (r, k) => (edits[r.id] && edits[r.id][k] !== undefined ? edits[r.id][k] : orig(r)[k]);
  const setCell = (r, k, v) => setEdits((e) => ({ ...e, [r.id]: { ...(e[r.id] || {}), [k]: v } }));
  const dirtyIds = Object.keys(edits).filter((id) => {
    const r = rows.find((x) => x.id === id); if (!r) return false;
    const o = orig(r), e = edits[id];
    return OUTREACH_KEYS.some((k) => e[k] !== undefined && (e[k] ?? "") !== (o[k] ?? ""));
  });
  const saveAll = async () => {
    if (!dirtyIds.length) return;
    const updates = dirtyIds.map((id) => {
      const r = rows.find((x) => x.id === id);
      return { id, email: cell(r, "email"), contact_person: cell(r, "contact_person"), notes: cell(r, "notes"), last_contacted: cell(r, "last_contacted") };
    });
    setSavingBatch(true);
    try {
      const { data } = await api.patch("/prospects/batch", { updates });
      toast.success(`Saved ${data.updated} prospect${data.updated === 1 ? "" : "s"}`);
      setEdits({}); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingBatch(false); }
  };

  const cleanup = async () => {
    if (!window.confirm("Clean up prospects now?\n\nThis removes duplicate records (same location) and assigns each un-zoned prospect to the zone that contains it. Outreach notes/emails are merged, not lost.")) return;
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/prospects-repair");
      toast.success(`Clean-up done — removed ${data.removed_total ?? data.removed} duplicate${(data.removed_total ?? data.removed) === 1 ? "" : "s"}, assigned ${data.assigned} to a zone`);
      setReport({
        kind: "Clean-up",
        rows: [
          ["Bike shops before cleanup", data.bike_shops_before],
          ["Bike shops after cleanup", data.bike_shops_after],
          ["Records merged (total)", data.removed_total ?? data.removed],
          ["· same-location duplicates", data.removed],
          ["· same business (name + address/phone)", data.merged_identity ?? 0],
          ["Prospects assigned to a zone", data.assigned],
          ["States normalized", data.states_set ?? 0],
        ],
      });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCleaning(false); }
  };

  const exportCsv = () => {
    if (!rows.length) return;
    const cols = [
      ["Business", "name"], ["Type", "type"], ["Category", "category"],
      ["Street Address", "addrStreet"], ["City", "addrCity"], ["State", (r) => r.addrState || r.state], ["Zip Code", "addrZip"],
      ["Phone", "phone"], ["Website", "website"], ["Hours", "hours"],
      ["Email", "email"], ["Contact", "contactPerson"],
      ["Bike Path", "bikePathName"], ["Zone #", "zoneNumber"], ["Zone", "zoneName"],
      ["Status", "status"], ["Data Quality", "completeness"], ["Last Contacted", "lastContacted"],
      ["Latitude", "lat"], ["Longitude", "lng"], ["Notes", "notes"],
    ];
    const esc = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = cols.map((c) => c[0]).join(",");
    const picked = selected.size ? rows.filter((r) => selected.has(r.id)) : rows;
    const lines = picked.map((r) => cols.map((c) => esc(typeof c[1] === "function" ? c[1](r) : r[c[1]])).join(","));
    const csv = [header, ...lines].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `prospects-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} prospect${rows.length === 1 ? "" : "s"} to CSV`);
  };

  const runCorridor = async () => {
    if (!corridorBp) { toast.error("Pick a bike path first"); return; }
    setCorridorLoading(true); setCorridorData(null);
    try {
      const { data } = await api.get("/admin/trails/bike-shops/corridor-preview", { params: { bike_path_id: corridorBp } });
      setCorridorData(data);
      if (data.osm_status !== "ok") toast.error(data.message || "Overpass unavailable — retry");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCorridorLoading(false); }
  };

  const harvestShops = async () => {
    if (!corridorBp) { toast.error("Pick a bike path first"); return; }
    if (!window.confirm("Harvest bike shops within 5 miles of this bike path's corridor?\n\nNew bike shops are added as prospects (existing ones are refreshed). Restaurants, breweries, hotels and attractions are not affected, and bike shops never count toward opportunity or leasability.")) return;
    setHarvesting(true);
    try {
      const { data } = await api.post("/admin/trails/bike-shops/harvest", { bike_path_id: corridorBp, buffer_miles: 5 });
      if (data.osm_status !== "ok") { toast.error(data.message || "Overpass unavailable — retry"); }
      else {
        toast.success(`Harvested ${data.found} bike shop${data.found === 1 ? "" : "s"} within 5 mi — ${data.inserted} new, ${data.updated} updated`);
        setReport({
          kind: `Harvest — ${data.bike_path}`,
          rows: [
            ["Bike shops (all) before", data.total_before],
            ["Bike shops (all) after", data.total_after],
            ["New bike shops inserted", data.inserted],
            ["Existing bike shops enriched", data.updated],
            [`Final count for ${data.bike_path}`, data.path_after],
          ],
          enriched: data.enriched_names || [],
        });
        setCorridorOpen(false);
        load();
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setHarvesting(false); }
  };

  const zoneLabel = (z) => `#${z.zoneNumber}${z.zoneName ? ` — ${z.zoneName}` : ""}`;
  const bikePathOpts = filters.bikePaths.filter((b) => f.state === ALL || b.state === f.state);
  const zoneOpts = filters.zones.filter((z) => (f.state === ALL || z.state === f.state) && (f.bike_path_id === ALL || z.bikePathId === f.bike_path_id));
  const located = rows.filter((r) => typeof r.lat === "number" && typeof r.lng === "number");
  const unlocatedCount = rows.length - located.length;
  const fLat = dialog?.form?.latitude, fLng = dialog?.form?.longitude;
  const hasCoords = fLat !== "" && fLat != null && fLng !== "" && fLng != null;
  const dialogRow = dialog?.id ? rows.find((x) => x.id === dialog.id) : null;

  return (
    <div className="space-y-5" data-testid="prospects-panel">
      <ContactHistoryDialog prospect={historyFor} onClose={() => setHistoryFor(null)} />
      <MailingLabelsDialog open={labelsOpen} onOpenChange={setLabelsOpen} filteredIds={rows.map((r) => r.id)} selectedIds={rows.filter((r) => selected.has(r.id)).map((r) => r.id)} onFillZips={() => { setLabelsOpen(false); setZipsOpen(true); }} />
      <BulkEmailDialog prospects={bulkEmail} onClose={(n) => { setBulkEmail(null); if (n) load(); }} />
      <ImportProspectsDialog open={importOpen} onOpenChange={setImportOpen} onDone={() => { load(); }} />
      <FillZipsDialog open={zipsOpen} onOpenChange={setZipsOpen} onDone={() => { if (!isAdmin || hasActiveFilter) load(); }} />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-bold">Prospects</h2>
          <p className="text-sm text-muted-foreground">Your outreach workspace — edit contact details inline across many rows, then save once.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-border overflow-hidden" data-testid="prospects-view-toggle">
            <button type="button" onClick={() => setView("list")} className={`px-3 py-1.5 transition-colors ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} title="List view" data-testid="prospects-view-list"><List className="w-4 h-4" /></button>
            <button type="button" onClick={() => setView("map")} className={`px-3 py-1.5 transition-colors ${view === "map" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} title="Map view" data-testid="prospects-view-map"><MapIcon className="w-4 h-4" /></button>
          </div>
          <Button onClick={openAdd} data-testid="prospect-add-btn"><Plus className="w-4 h-4 mr-1" />Add Prospect</Button>
          <Button variant="outline" onClick={exportCsv} disabled={!rows.length} data-testid="prospects-export-btn" title={selected.size ? "Download only the ticked prospects as a CSV spreadsheet" : "Download the prospects currently shown as a CSV spreadsheet (tick rows to export only those)"}>
            <Download className="w-4 h-4 mr-1" />Export CSV{selected.size ? ` (${selected.size})` : ""}
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="prospects-import-btn" title="Add or update prospects in bulk from a CSV spreadsheet">
            <Upload className="w-4 h-4 mr-1" />Import CSV
          </Button>
          <Button variant="outline" onClick={() => setLabelsOpen(true)} data-testid="prospects-export-labels-btn" title="Create print-ready Avery mailing labels">
            <Tags className="w-4 h-4 mr-1" />Export Mailing Labels{selected.size ? ` (${selected.size})` : ""}
          </Button>
          <Button variant="outline" onClick={() => setBulkEmail(rows.filter((r) => selected.has(r.id)))} disabled={!selected.size} data-testid="prospects-bulk-email-btn" title="Tick prospects in the list, then step through a personalized email for each one">
            <Mails className="w-4 h-4 mr-1" />Email Selected{selected.size ? ` (${selected.size})` : ""}
          </Button>
          <Button variant="outline" onClick={() => setZipsOpen(true)} data-testid="prospects-fill-zips-btn" title="Look up missing ZIP codes from each prospect's map location">
            <MapPin className="w-4 h-4 mr-1" />Fill Missing ZIP Codes
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={cleanup} disabled={cleaning} data-testid="prospects-cleanup-btn" title="Remove duplicate prospects and assign un-zoned prospects to their zone">
              {cleaning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wrench className="w-4 h-4 mr-1" />}Clean up
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={() => { setCorridorOpen(true); setCorridorData(null); }} data-testid="corridor-preview-btn" title="Compare zone-based vs corridor-based bike shop counts (read-only)">
              <Route className="w-4 h-4 mr-1" />Corridor preview
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect label="State" value={f.state} onChange={(v) => setF({ ...f, state: v, bike_path_id: ALL, zone_number: ALL })} options={filters.states.map((s) => ({ value: s, label: s }))} testid="prospect-filter-state" />
        <FilterSelect label="Bike Path" value={f.bike_path_id} onChange={(v) => setF({ ...f, bike_path_id: v, zone_number: ALL })} options={bikePathOpts.map((b) => ({ value: b.id, label: b.name }))} testid="prospect-filter-bikepath" />
        <FilterSelect label="Zone #" value={f.zone_number} onChange={(v) => setF({ ...f, zone_number: v })} options={zoneOpts.map((z) => ({ value: String(z.zoneNumber), label: zoneLabel(z) }))} testid="prospect-filter-zone" />
        <FilterSelect label="Type" value={f.type} onChange={(v) => setF({ ...f, type: v })} options={filters.types.map((t) => ({ value: t, label: t }))} testid="prospect-filter-type" />
        <FilterSelect label="Status" value={f.status} onChange={(v) => setF({ ...f, status: v })} options={filters.statuses.map((s) => ({ value: s, label: s }))} testid="prospect-filter-status" />
        <FilterSelect label="Quality" value={f.quality} onChange={(v) => setF({ ...f, quality: v })} options={[{ value: "rich", label: "Rich (3+ fields)" }, { value: "partial", label: "Partial" }, { value: "thin", label: "Thin (name only)" }]} testid="prospect-filter-quality" />
        <FilterSelect label="Filled from map" value={f.filled} onChange={(v) => setF({ ...f, filled: v })} options={[{ value: "any", label: "To check: all filled" }, { value: "street", label: "To check: street found" }, { value: "city", label: "To check: city filled" }, { value: "zip", label: "To check: ZIP only" }, { value: "checked", label: "Already checked" }]} testid="prospect-filter-filled" />
        <FilterSelect label="Follow-up" value={f.follow_up} onChange={(v) => setF({ ...f, follow_up: v })} options={[{ value: "due", label: "Follow-up due" }]} testid="prospect-filter-followup" />
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name…" className="h-9 w-48 pl-8" data-testid="prospect-search" />
        </div>
      </div>

      {view === "list" && (
      <>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-muted-foreground" data-testid="prospects-dirty-count">
            {dirtyIds.length > 0 ? `${dirtyIds.length} unsaved change${dirtyIds.length === 1 ? "" : "s"}` : "Click any Email, Contact, Notes or Last Contacted cell to edit"}
          </div>
          <Button onClick={saveAll} disabled={dirtyIds.length === 0 || savingBatch} data-testid="prospects-save-changes">
            {savingBatch ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save Changes{dirtyIds.length > 0 ? ` (${dirtyIds.length})` : ""}
          </Button>
        </div>
        <div className="rounded-xl border border-border overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-10"><Checkbox checked={allChecked} disabled={!rows.length} onCheckedChange={(v) => setSelected(v ? new Set(rows.map((r) => r.id)) : new Set())} aria-label="Select all" data-testid="prospects-select-all" /></TableHead>
              <TableHead>Business</TableHead><TableHead>Address</TableHead><TableHead>Phone</TableHead>
              <TableHead>Email</TableHead><TableHead>Contact</TableHead>
              <TableHead className="min-w-[320px]">Notes</TableHead><TableHead>Last Contacted</TableHead>
              <TableHead className="text-right sticky right-0 bg-card z-20 shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.25)]">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline text-primary" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground" data-testid="prospects-empty">{isAdmin && !hasActiveFilter ? "Choose a filter above (state, bike path, zone, type or status) or search by name to load prospects." : "No prospects found. Add one or adjust filters."}</TableCell></TableRow>
              ) : rows.map((r) => {
                const isDirty = dirtyIds.includes(r.id);
                return (
                <TableRow key={r.id} data-testid={`prospect-row-${r.id}`} className={isDirty ? "bg-primary/5" : undefined}>
                  <TableCell className="align-top pt-3 w-10"><Checkbox checked={selected.has(r.id)} onCheckedChange={(v) => toggleRow(r.id, !!v)} aria-label={`Select ${r.name}`} data-testid={`prospect-select-${r.id}`} /></TableCell>
                  <TableCell className="font-medium whitespace-nowrap align-top pt-3">
                    <div className="flex items-center gap-1.5">
                      <span>{r.name}</span>
                      {r.riderSaved && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-400 text-[10px] font-semibold px-2 py-0.5" title="Published to the rider map" data-testid={`prospect-rider-tag-${r.id}`}>
                          <MapPinned className="w-3 h-3" />On Rider map
                        </span>
                      )}
                      {r.completeness === "thin" && (
                        <span className="inline-flex items-center rounded-full bg-amber-500/15 text-amber-400 text-[10px] font-semibold px-2 py-0.5" title="Name only — no address, phone or website yet" data-testid={`prospect-thin-tag-${r.id}`}>Thin</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[220px] align-top pt-3" title={r.address}>
                    <div className="truncate">{r.address || "—"}</div>
                    {r.filledFrom && !r.addressChecked && (
                      <div className="mt-1 flex flex-wrap items-center gap-1.5" data-testid={`prospect-filled-${r.id}`}>
                        <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 text-sky-400 text-[10px] font-semibold px-2 py-0.5" title={`Was: ${r.originalAddress || "(blank)"}`} data-testid={`prospect-filled-tag-${r.id}`}><MapPin className="w-3 h-3" />{r.filledFrom === "street" ? "Street from map" : r.filledFrom === "city" ? "City from map" : "ZIP from map"}</span>
                        <button type="button" onClick={() => markChecked(r)} className="text-[10px] font-semibold text-emerald-400 hover:underline" data-testid={`prospect-mark-checked-${r.id}`}>Looks right</button>
                      </div>
                    )}
                    {r.filledFrom && !r.addressChecked && <div className="text-[10px] text-muted-foreground truncate" data-testid={`prospect-original-address-${r.id}`}>Was: {r.originalAddress || "(blank)"}</div>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap align-top pt-3">{r.phone || "—"}</TableCell>
                  <TableCell className="align-top p-1.5">
                    <Input value={cell(r, "email")} onChange={(e) => setCell(r, "email", e.target.value)} placeholder="email@…" className="h-8 w-[180px] text-sm" data-testid={`prospect-email-${r.id}`} />
                  </TableCell>
                  <TableCell className="align-top p-1.5">
                    <Input value={cell(r, "contact_person")} onChange={(e) => setCell(r, "contact_person", e.target.value)} placeholder="Name" className="h-8 w-[140px] text-sm" data-testid={`prospect-contact-${r.id}`} />
                  </TableCell>
                  <TableCell className="align-top p-1.5">
                    <Textarea value={cell(r, "notes")} onChange={(e) => setCell(r, "notes", e.target.value)} placeholder="Outreach notes…" rows={2} className="min-w-[320px] text-sm resize-y" data-testid={`prospect-notes-${r.id}`} />
                  </TableCell>
                  <TableCell className="align-top p-1.5">
                    <Input type="date" value={cell(r, "last_contacted")} onChange={(e) => setCell(r, "last_contacted", e.target.value)} className="h-8 w-[140px] text-sm" data-testid={`prospect-lastcontacted-${r.id}`} />
                    {r.followUpDue && !CLOSED.has(r.status) && (
                      <div className={`mt-1 text-[10px] font-semibold ${r.followUpDue < TODAY ? "text-rose-400" : r.followUpDue === TODAY ? "text-amber-400" : "text-muted-foreground"}`} title={r.lastOutreach ? `Last: ${r.lastOutreach.channel}${r.lastOutreach.template ? ` (${r.lastOutreach.template})` : ""}` : ""} data-testid={`prospect-followup-${r.id}`}>
                        {r.followUpDue <= TODAY ? "Follow-up due" : "Follow up"} {new Date(`${r.followUpDue}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className={`text-right whitespace-nowrap align-top pt-2 sticky right-0 shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.25)] ${isDirty ? "bg-[#1b2330]" : "bg-card"}`}>
                    <Button size="icon" variant="ghost" className="h-7 w-7 relative" onClick={() => setHistoryFor(r)} title="Contact history" data-testid={`prospect-history-${r.id}`}>
                      <History className="w-4 h-4" />
                      {r.outreachCount > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-0.5 rounded-full bg-primary text-primary-foreground text-[9px] font-bold leading-[15px]" data-testid={`prospect-history-count-${r.id}`}>{r.outreachCount}</span>}
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(r)} title="Edit prospect" data-testid={`prospect-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10" onClick={() => remove(r)} title="Delete prospect" data-testid={`prospect-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </>
      )}

      {view === "map" && (
        <div className="rounded-xl border border-border overflow-hidden" data-testid="prospects-map">
          {loading ? (
            <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-primary" /></div>
          ) : located.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground text-sm" data-testid="prospects-map-empty">{isAdmin && !hasActiveFilter ? "Choose a filter above (state, bike path, zone, type or status) or search by name to load prospects." : "No prospects have map coordinates yet."}</div>
          ) : (
            <LeafletMap height={540}>
              <FitBounds bounds={located.map((r) => [r.lat, r.lng])} nonce={located.length} />
              {located.map((r) => (
                <CircleMarker key={r.id} center={[r.lat, r.lng]} radius={7} pathOptions={{ color: "#fff", weight: 1.5, fillColor: TYPE_COLORS[r.type] || "#94a3b8", fillOpacity: 0.95 }} data-testid={`prospect-pin-${r.id}`}>
                  <Popup>
                    <div className="space-y-1 text-xs min-w-[150px]">
                      <div className="font-bold text-sm">{r.name}</div>
                      <div className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ background: TYPE_COLORS[r.type] || "#94a3b8" }} />{r.type} · {r.status}</div>
                      {r.phone && <div>{r.phone}</div>}
                      {r.address && <div className="text-muted-foreground">{r.address}</div>}
                      {r.zoneNumber != null && <div className="text-muted-foreground">Zone #{r.zoneNumber}{r.bikePathName ? ` · ${r.bikePathName}` : ""}</div>}
                      <button type="button" onClick={() => openEdit(r)} className="text-primary underline pt-1" data-testid={`prospect-pin-edit-${r.id}`}>Edit prospect</button>
                      {r.type === "Attraction" && (
                        <div className="pt-1.5 mt-1.5 border-t border-gray-200">
                          {r.riderSaved ? (
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-emerald-600 font-semibold" data-testid={`prospect-pin-on-rider-${r.id}`}><MapPinned className="w-3.5 h-3.5" />On the Rider map</div>
                              <button type="button" onClick={() => removeFromRider(r)} disabled={riderBusy === r.id} className="text-red-600 underline disabled:opacity-50" data-testid={`prospect-pin-remove-rider-${r.id}`}>{riderBusy === r.id ? "Removing…" : "Remove from Rider map"}</button>
                            </div>
                          ) : (isAdmin || r.zoneId) ? (
                            <button type="button" onClick={() => addToRider(r)} disabled={riderBusy === r.id} className="flex items-center gap-1 text-primary underline font-medium disabled:opacity-50" data-testid={`prospect-pin-add-rider-${r.id}`}><MapPinned className="w-3.5 h-3.5" />{riderBusy === r.id ? "Adding…" : "Add to Rider map"}</button>
                          ) : (
                            <div className="text-gray-500" data-testid={`prospect-pin-needs-zone-${r.id}`}>Assign a zone to publish to the Rider map.</div>
                          )}
                        </div>
                      )}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </LeafletMap>
          )}
          {unlocatedCount > 0 && <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border" data-testid="prospects-map-unlocated">{unlocatedCount} prospect{unlocatedCount === 1 ? "" : "s"} without map coordinates {unlocatedCount === 1 ? "is" : "are"} not shown.</div>}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="prospect-dialog">
          <DialogHeader><DialogTitle>{dialog?.mode === "add" ? "Add Prospect" : "Edit Prospect"}</DialogTitle><DialogDescription className="sr-only">Prospect details for outreach</DialogDescription></DialogHeader>
          {dialog && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Business Name" className="sm:col-span-2"><Input value={dialog.form.name} onChange={(e) => upd("name", e.target.value)} data-testid="prospect-field-name" /></Field>
              <Field label="Address" className="sm:col-span-2"><Input value={dialog.form.address} onChange={(e) => upd("address", e.target.value)} data-testid="prospect-field-address" /></Field>
              <Field label="Phone"><Input value={dialog.form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="prospect-field-phone" /></Field>
              <Field label="Email"><Input value={dialog.form.email} onChange={(e) => upd("email", e.target.value)} data-testid="prospect-field-email" /></Field>
              <Field label="Contact Person"><Input value={dialog.form.contact_person} onChange={(e) => upd("contact_person", e.target.value)} data-testid="prospect-field-contact" /></Field>
              <Field label="Type">
                <Select value={dialog.form.type} onValueChange={(v) => upd("type", v)}>
                  <SelectTrigger data-testid="prospect-field-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{filters.types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Bike Path">
                <Select value={dialog.form.bike_path_id || "none"} onValueChange={(v) => upd("bike_path_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="prospect-field-bikepath"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{filters.bikePaths.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Zone #">
                <Select value={dialog.form.zone_id || "none"} onValueChange={(v) => upd("zone_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="prospect-field-zone"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{filters.zones.map((z) => <SelectItem key={z.zoneId} value={z.zoneId}>{zoneLabel(z)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={dialog.form.status} onValueChange={(v) => upd("status", v)}>
                  <SelectTrigger data-testid="prospect-field-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{filters.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Last Contacted"><Input type="date" value={dialog.form.last_contacted} onChange={(e) => upd("last_contacted", e.target.value)} data-testid="prospect-field-lastcontacted" /></Field>
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs text-muted-foreground">Location {hasCoords ? "" : "(optional)"}</Label>
                  <Button type="button" size="sm" variant="outline" className="h-7" onClick={geocode} disabled={geocoding} data-testid="prospect-geocode-btn">{geocoding ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MapPin className="w-3 h-3 mr-1" />}Locate from address</Button>
                </div>
                <div className="rounded-lg border border-border overflow-hidden" data-testid="prospect-dialog-map">
                  <LeafletMap key={`pm-${mapKey}`} height={200} zoom={hasCoords ? 14 : 10} center={hasCoords ? [Number(fLat), Number(fLng)] : [41.72, -71.28]} onClick={(lat, lng) => setCoords(lat, lng)}>
                    {hasCoords && <CircleMarker center={[Number(fLat), Number(fLng)]} radius={8} pathOptions={{ color: "#fff", weight: 2, fillColor: "#EF4444", fillOpacity: 1 }} />}
                  </LeafletMap>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">{hasCoords ? `Pinned at ${Number(fLat).toFixed(5)}, ${Number(fLng).toFixed(5)} — click the map to adjust.` : "No location yet. Use \u201cLocate from address\u201d or click the map to drop a pin."}</p>
              </div>
              <Field label="Notes" className="sm:col-span-2"><Textarea value={dialog.form.notes} onChange={(e) => upd("notes", e.target.value)} rows={3} data-testid="prospect-field-notes" /></Field>
              {dialog.mode === "edit" && (
                <div className="sm:col-span-2 rounded-lg border border-border p-3" data-testid="prospect-dialog-rider">
                  <div className="text-xs text-muted-foreground mb-2">Rider map — creates a pin & banner for riders (pending admin approval).{isAdmin ? " The nearest zone is assigned automatically." : ""} Attach a photo below so the banner is ready on approval.</div>
                  {dialog.form.type !== "Attraction" ? (
                    <div className="text-xs text-amber-500" data-testid="prospect-dialog-rider-nottraction">Only <span className="font-semibold">Attraction</span> prospects can be published to the rider map. Set <span className="font-semibold">Type</span> to <span className="font-semibold">Attraction</span> and save, then add it.</div>
                  ) : dialogRow?.riderSaved ? (
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-sm text-emerald-400" data-testid="prospect-dialog-on-rider"><MapPinned className="w-4 h-4" />On the Rider map</span>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => removeFromRider({ id: dialog.id })} disabled={riderBusy === dialog.id} data-testid="prospect-dialog-remove-rider">{riderBusy === dialog.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}Remove</Button>
                    </div>
                  ) : !(dialogRow?.lat != null && dialogRow?.lng != null) ? (
                    <div className="text-xs text-amber-500" data-testid="prospect-dialog-rider-noloc">Drop a pin on the map (or use “Locate from address”) and save first, then re-open to add it to the Rider map.</div>
                  ) : (!isAdmin && !dialogRow?.zoneId) ? (
                    <div className="text-xs text-amber-500" data-testid="prospect-dialog-rider-nozone">Assign this attraction to one of your leased zones (and save) first, then add it to the Rider map.</div>
                  ) : (
                    <div className="space-y-2">
                      <PhotoUpload photos={riderPhotos} onChange={setRiderPhotos} label="Add photo" testId="prospect-dialog-rider-photos" />
                      <p className="text-[11px] text-muted-foreground">Optional — a photo completes the rider banner on approval.</p>
                      <Button variant="outline" size="sm" onClick={() => addToRider({ id: dialog.id })} disabled={riderBusy === dialog.id} data-testid="prospect-dialog-add-rider">{riderBusy === dialog.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPinned className="w-4 h-4 mr-2" />}Add to Rider map</Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} data-testid="prospect-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="prospect-save">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={corridorOpen} onOpenChange={setCorridorOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="corridor-preview-dialog">
          <DialogHeader>
            <DialogTitle>Bike shops: Corridor vs Zone (read-only)</DialogTitle>
            <DialogDescription>How many OSM bike shops fall inside this path's zones vs within 2 / 5 / 10 miles of the trail corridor. Nothing is saved.</DialogDescription>
          </DialogHeader>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground mb-1 block">Bike path</Label>
              <Select value={corridorBp} onValueChange={setCorridorBp}>
                <SelectTrigger data-testid="corridor-bikepath-select"><SelectValue placeholder="Choose a bike path…" /></SelectTrigger>
                <SelectContent>{filters.bikePaths.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={runCorridor} disabled={corridorLoading || !corridorBp} data-testid="corridor-run-btn">{corridorLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Run comparison</Button>
            <Button variant="secondary" onClick={harvestShops} disabled={harvesting || !corridorBp} data-testid="corridor-harvest-btn" title="Harvest bike shops within 5 miles of this corridor into prospects">{harvesting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Route className="w-4 h-4 mr-1" />}Harvest 5 mi</Button>
          </div>
          {corridorData && corridorData.osm_status === "ok" && (
            <div className="space-y-3" data-testid="corridor-results">
              <div className="grid grid-cols-4 gap-2 text-center">
                {[["Zone-based", "zone_based"], ["2 mi", "corridor_2mi"], ["5 mi", "corridor_5mi"], ["10 mi", "corridor_10mi"]].map(([lbl, k]) => (
                  <div key={k} className="rounded-lg border border-border p-3">
                    <div className="text-2xl font-bold tabular-nums">{corridorData.counts[k]}</div>
                    <div className="text-xs text-muted-foreground">{lbl}</div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground">{corridorData.bike_path} · {corridorData.zone_count} zone(s) on this path · showing shops within 10 mi</div>
              <div className="rounded-lg border border-border max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card"><tr className="text-left text-xs text-muted-foreground"><th className="p-2">Bike shop</th><th className="p-2">Distance</th><th className="p-2">In zone</th></tr></thead>
                  <tbody>
                    {corridorData.samples.corridor_10mi.map((s, i) => (
                      <tr key={i} className="border-t border-border"><td className="p-2">{s.name}</td><td className="p-2 tabular-nums">{s.distance_miles} mi</td><td className="p-2">{s.in_zone || "—"}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {corridorData && corridorData.osm_status !== "ok" && (
            <div className="text-sm text-amber-400" data-testid="corridor-unavailable">{corridorData.message || "Overpass unavailable — please retry."}</div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!report} onOpenChange={(o) => !o && setReport(null)}>
        <DialogContent className="max-w-lg" data-testid="prospects-report-dialog">
          <DialogHeader>
            <DialogTitle>{report?.kind} — summary</DialogTitle>
            <DialogDescription>Results of the last operation.</DialogDescription>
          </DialogHeader>
          {report && (
            <div className="space-y-4" data-testid="prospects-report-body">
              <div className="rounded-lg border border-border divide-y divide-border">
                {report.rows.map(([label, val], i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-semibold tabular-nums" data-testid={`report-val-${i}`}>{val ?? 0}</span>
                  </div>
                ))}
              </div>
              {report.enriched && report.enriched.length > 0 && (
                <div>
                  <div className="text-sm font-medium mb-1">Skipped — matched existing records ({report.enriched.length})</div>
                  <div className="text-xs text-muted-foreground">These shops were enriched in place (coords, path, distance) instead of being added again:</div>
                  <div className="mt-2 rounded-lg border border-border max-h-48 overflow-y-auto divide-y divide-border" data-testid="report-enriched-list">
                    {report.enriched.map((n, i) => (
                      <div key={i} className="px-3 py-1.5 text-sm">{n}</div>
                    ))}
                  </div>
                </div>
              )}
              {report.enriched && report.enriched.length === 0 && (
                <div className="text-xs text-muted-foreground">No existing records matched — every discovered shop was newly inserted.</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-auto min-w-[130px] text-sm" data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">{label}: All</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <Label className="text-xs text-muted-foreground mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
